/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useState } from 'react';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import AddIcon from '@material-ui/icons/Add';
import { useSelector } from 'react-redux';
import { Grid, makeStyles } from '@material-ui/core';
import FloatButton from '../../sidebar/FloatButton';
import ShowProgress from '../../ui/Backdrop';
import useFetchData from '../hooks/useFetchData';
import AddSeries from './AddSeries';
import SearchBox from '../../shared/SearchBox';
import Theme from '../../ui/Theme';
import { getStatusColor } from '../utils';

const useStyles = makeStyles({
  root: {
    height: '54vh',
    overflowY: 'auto',
    backgroundColor: Theme.palette.background.paper,
    '&::-webkit-scrollbar': { width: '10px' },
    '&::-webkit-scrollbar-track': {
      boxShadow: 'inset 0 0 5px grey',
      borderRadius: '20px',
    },
    '&::-webkit-scrollbar-thumb': {
      background: 'lightGrey',
      borderRadius: '100px',
    },
  },
  searchBar: {
    maxHeight: '5vh',
    margin: '10px 0',
    padding: '4px',
  },
  searchText: {
    width: '93%',
    height: '42px',
    background: '#FAFAFA',
    border: '2px solid #E2E2E2',
    padding: '8px',
    fontFamily: 'Montserrat',
    fontWeight: '500',
    fontSize: '14px',
  },
  featherPlus: {
    width: '36px',
    height: '42px',
    background: '#FAFAFA',
  },
  featherPlusSelected: {
    width: '36px',
    height: '42px',
    background: '#D3D3D3',
  },
  listTextSelected: {
    fontSize: '14px',
    fontWeight: 600,
    lineHeight: '17px',
    fontStyle: 'normal',
    padding: '8px 16px 0px 0px',
    maxWidth: '100%',
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 2,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
  },

  listTextNonSelected: {
    fontSize: '14px',
    fontWeight: 500,
    lineHeight: '17px',
    fontStyle: 'normal',
    maxWidth: '100%',
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 2,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
  },
  listSubTextSelected: {
    color: Theme.palette.primary.main,
    fontSize: '12px',
    fontWeight: 500,
    fontStyle: 'normal',
    padding: '0px 16px 8px 0px',
  },
  listSubTextNonSelected: {
    color: 'rgba(0, 0, 0, 0.4)',
    fontSize: '12px',
    fontWeight: 500,
    fontStyle: 'normal',
    lineHeight: '15px',
    padding: '0px 16px 8px 0px',
  },
  listSubTextJustify: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  secondaryTextRed: {
    color: 'rgba(255, 0, 0, 1) !important',
  },
  highlightItem: {
    borderRight: `4px solid ${Theme.palette.primary.main}`,
  },
  menuLayout: {
    minWidth: 250,
    padding: '8px 0',
    borderRadius: '8px',
  },
  menuItem: {
    padding: '0px 16px',
    textAlign: 'left',
  },
});

const SidebarListQuiz = () => {
  const classes = useStyles();
  const [filterArray, setFilterArray] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const selectedId = useSelector((state) => state?.getQuizSeries?.selectedId);
  const list = useSelector((state) => state?.getQuizSeriesList?.quizSeriesList) || [];
  const { fetchQuizSeriesList, fetchQuizSeriesOveview } = useFetchData();

  const myRefs = filterArray?.reduce((acc, val) => {
    acc[val.id] = React.createRef();
    return acc;
  }, {});

  useEffect(() => {
    if (myRefs[selectedId]) {
      myRefs[selectedId].current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [selectedId, myRefs]);

  useEffect(() => {
    fetchQuizSeriesList();
  }, [fetchQuizSeriesList]);

  useEffect(() => {
    setFilterArray(list);
  }, [list]);

  useEffect(() => {
    if (!list) return;

    const lowerSearch = searchTerm?.toLowerCase() || '';

    const filtered = searchTerm ? list.filter((item) => item.name?.toLowerCase().includes(lowerSearch)) : list;

    const sorted = [...filtered].sort((a, b) => {
      const aName = a.name?.toLowerCase() || '';
      const bName = b.name?.toLowerCase() || '';

      const aStarts = aName.startsWith(lowerSearch);
      const bStarts = bName.startsWith(lowerSearch);

      if (aStarts && !bStarts) return -1;
      if (!aStarts && bStarts) return 1;
      return 0;
    });

    setFilterArray(sorted);
  }, [searchTerm, list]);

  const togglePopup = () => setIsPopupOpen((prev) => !prev);
  const addNewQuizSeries = () => {
    setIsPopupOpen(true);
  };
  const handleSearchQuizSeries = useCallback(({ target: { value } }) => setSearchTerm(value), []);

  const renderListItem = (data) => {
    const isSelected = selectedId === data.id;
    const handleListItemClick = (id) => {
      fetchQuizSeriesOveview(id);
    };
    return (
      <div key={data.id} className={isSelected ? classes.highlightItem : ''}>
        <ListItem
          button
          selected={isSelected}
          className={classes.listStyling}
          onClick={() => handleListItemClick(data.id)}
          ref={myRefs[data.id]}
        >
          <ListItemText
            primary={<p className={isSelected ? classes.listTextSelected : classes.listTextNonSelected}>{data.name}</p>}
            secondary={
              <div className={isSelected ? classes.listSubTextSelected : classes.listSubTextNonSelected}>
                <div className={classes.listSubTextJustify}>
                  <div>{`${data.startDate} - ${data.endDate}`}</div>
                  <div style={{ color: getStatusColor(data?.status?.text) }}>{`${data?.status?.text}`}</div>
                </div>
              </div>
            }
          />
        </ListItem>
      </div>
    );
  };

  return (
    <Grid container direction="column">
      <Grid item>
        <SearchBox
          handleSearchChange={handleSearchQuizSeries}
          searchVal={searchTerm}
          placeholder="Search Quiz Series"
          menu={false}
          filterCondition={{}}
        />
      </Grid>
      <Grid item>
        <div className={classes.root}>
          <List component="nav" aria-label="secondary mailbox folder">
            {filterArray?.map(renderListItem)}
          </List>
          <FloatButton
            fabs={[{ color: 'primary', className: classes.fab, icon: <AddIcon />, label: 'Add' }]}
            value={0}
            mainTabID={22}
            onaddNewQuizSeries={addNewQuizSeries}
          />
          {isPopupOpen && <AddSeries isOpen close={togglePopup} />}
          <ShowProgress />
        </div>
      </Grid>
    </Grid>
  );
};

export default SidebarListQuiz;
